import { ComponentPortal } from "@angular/cdk/portal";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { effect, inject, Injectable, Injector } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { scan } from "rxjs";
import { RecordingStateService } from "./recording-active/recording-state.service";

@Injectable({ providedIn: "root" })
export class RecordingOverlayService {
    private readonly _overlay = inject(Overlay);
    private readonly _injector = inject(Injector);
    private readonly _state = inject(RecordingStateService);
    private readonly _matDialog = inject(MatDialog);
    private _overlayRef?: OverlayRef;

    constructor() {
        // Increments each time a dialog opens; used to trigger the effect so the popup
        // is recreated on top of the new dialog's backdrop (CDK stacks by DOM insertion order).
        const dialogOpenedVersion = toSignal(
            this._matDialog.afterOpened.pipe(scan((n) => n + 1, 0)),
            { initialValue: 0 },
        );

        effect(() => {
            dialogOpenedVersion(); // track so effect reruns whenever a dialog opens
            if (this._state.isActive() && !this._state.recordingViewVisible() && !this._state.inAttendance()) {
                this._openOnTop();
            } else {
                this._close();
            }
        });
    }

    private _openOnTop(): void {
        // Dispose any existing ref so the new overlay is appended last in the CDK
        // container, ensuring it sits above any currently-open dialog backdrops.
        if (this._overlayRef) {
            this._overlayRef.detach();
            this._overlayRef.dispose();
            this._overlayRef = undefined;
        }
        this._overlayRef = this._overlay.create({
            positionStrategy: this._overlay.position().global().bottom("24px").left("24px"),
            hasBackdrop: false,
            panelClass: "recording-popup-overlay",
        });
        // Lazy import to avoid circular dep at module load time
        import("./recording-popup/recording-popup.component").then(({ RecordingPopupComponent }) => {
            if (this._overlayRef && !this._overlayRef.hasAttached()) {
                this._overlayRef.attach(new ComponentPortal(RecordingPopupComponent, null, this._injector));
            }
        });
    }

    private _close(): void {
        this._overlayRef?.detach();
        this._overlayRef?.dispose();
        this._overlayRef = undefined;
    }
}
